import { action, observable } from 'mobx';

import { IRootStore } from '@stores/index';
import { apiAuthCaller } from '@services/apiCaller';
import { getApiErrorMessage } from '@components/common/util/util';
import { ISourceStore } from './source';
import { IDestinationStore } from './destination';
import { SqlModelConnection } from './modelsList';

type Connection = {
  id: string;
  destinations: { id: string }[];
};
export interface IConnectionsStore {
  connections: ISourceConnections;
  rootStore: IRootStore;
  setConnections(sources: Connection[]): void;
  removeConnections(source: ISourceStore, destination: IDestinationStore): void;
}

interface ISourceConnections {
  [key: string]: string[];
}

const trackRemoveConnections = (source: ISourceStore, destination: IDestinationStore) => {
  window.rudderanalytics.track('connection deleted', {
    sourceId: source.id,
    sourceName: source.sourceDef.displayName,
    destId: destination.id,
    destName: destination.destinationDefinition.displayName,
  });
};

export class ConnectionsStore implements IConnectionsStore {
  @observable connections: ISourceConnections = {};

  rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  @action.bound
  public async setConnections(sources: Connection[]) {
    const connections: ISourceConnections = {};
    sources.forEach((source) => {
      connections[source.id] = source.destinations.map((dest) => dest.id);
    });
    this.connections = connections;
  }

  @action.bound
  public async removeConnections(source: ISourceStore, destination: IDestinationStore) {
    const {
      messagesStore,
      userStore: { token },
      modelListStore,
    } = this.rootStore;
    const sourceIds = [source.id];
    try {
      const res = await apiAuthCaller(token).post(`/destinations/${destination.id}/disconnect`, {
        sourceIds,
      });

      if (res.data.sqlModelConnection?.sqlModelId) {
        const {
          sqlModelConnection,
        }: {
          sqlModelConnection: SqlModelConnection;
        } = res.data;
        const sqlModel = modelListStore.getModelById(sqlModelConnection.sqlModelId);
        sqlModel?.setSourceIds(
          sqlModel.sourceIds.filter(
            (sourceId) => sourceId !== sqlModelConnection.connection.sourceId,
          ),
        );
      }
      if (res.data.destination) {
        destination.setConfig(res.data.destination);
      }
      messagesStore.showSuccessMessage('Connection removed successfully');
      trackRemoveConnections(source, destination);
      this.updateConnections(source, destination);
    } catch (err) {
      messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to disconnect source'));
    }
  }

  private updateConnections(source: ISourceStore, destination: IDestinationStore) {
    const destinations = this.connections[source.id];
    const remainingDestinations = destinations.filter(
      (destId: string) => destId !== destination.id,
    );
    if (remainingDestinations.length > 0) {
      this.connections[source.id] = remainingDestinations;
    } else {
      delete this.connections[source.id];
    }
  }
}
